import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CasesContext from '../../context/CasesContext';
import { fetchDrafts, publishCase } from '../../utils/caseDraft';
import { getCurrentUserEmail } from '../../utils/currentUser';
import { setLastCasesTab, peekLastCasesTab, clearLastCasesTab } from '../../utils/casesViewState';
import './SimulationCases.css';
import Footer from '../Footer';

function CaseCard({ simCase, onClick }) {
  return (
    <div className="case-card" onClick={onClick}>
      <div className="case-card-main">
        <div className="case-card-header">
          <span className="case-name">{simCase.name}</span>
          <div className="case-card-badges">
            {simCase.configured
              ? <span className="case-badge case-badge-configured">Checklist Configured</span>
              : <span className="case-badge case-badge-pending">Checklist Pending</span>
            }
          </div>
        </div>
        <span className="case-date">Added {simCase.createdAt}</span>
      </div>
    </div>
  );
}

function initialFromEmail(email) {
  return (email || '?').trim().charAt(0).toUpperCase();
}

function DraftCard({ draft, onClick, onPublish, publishing }) {
  // Google-Drive-style "people with access" — when I own it, that's just my
  // collaborators; when I'm a collaborator, the owner is relevant too since
  // it's not me.
  const sharedWith = draft.isOwner
    ? (draft.collaborators || [])
    : [draft.ownerEmail, ...(draft.collaborators || [])].filter(Boolean);
  const visibleAvatars = sharedWith.slice(0, 3);
  const overflowCount = sharedWith.length - visibleAvatars.length;

  return (
    <div className="case-card case-card-draft" onClick={onClick}>
      <div className="case-card-main">
        <div className="case-card-header">
          <span className="case-name">{draft.name}</span>
          <div className="case-card-badges">
            <span className="case-badge case-badge-draft">Draft</span>
          </div>
        </div>
        <span className="case-date">Added {draft.createdAt}</span>
        {sharedWith.length > 0 && (
          <div className="draft-shared-row" title={`Shared with ${sharedWith.join(', ')}`}>
            <span className="draft-shared-icon">👥</span>
            <div className="draft-avatar-stack">
              {visibleAvatars.map((email) => (
                <span key={email} className="draft-avatar-circle" title={email}>
                  {initialFromEmail(email)}
                </span>
              ))}
              {overflowCount > 0 && (
                <span className="draft-avatar-circle draft-avatar-overflow">+{overflowCount}</span>
              )}
            </div>
          </div>
        )}
      </div>
      <button
        className="draft-publish-btn"
        onClick={(e) => { e.stopPropagation(); onPublish(draft.id); }}
        disabled={publishing}
      >
        {publishing ? 'Publishing...' : 'Publish'}
      </button>
    </div>
  );
}

function SimulationCases() {
  const navigate = useNavigate();
  const { cases: uploadedCases, loading: loadingUploaded, fetchCases } = useContext(CasesContext);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [publishingId, setPublishingId] = useState(null);
  const [activeTab, setActiveTab] = useState(() => peekLastCasesTab() === 'draft' ? 'draft' : 'published');

  // Clear the flag as a real (idempotent) side effect, not as part of the
  // useState initializer — see casesViewState.js for why.
  useEffect(() => {
    clearLastCasesTab();
  }, []);

  const goToCase = (caseId, tab) => {
    setLastCasesTab(tab);
    navigate(`/admin/cases/${caseId}`);
  };

  const loadDrafts = useCallback(() => {
    const userEmail = getCurrentUserEmail();
    if (!userEmail) { setLoadingDrafts(false); return; }
    setLoadingDrafts(true);
    fetchDrafts(userEmail)
      .then((res) => { if (Array.isArray(res.data)) setDrafts(res.data); })
      .catch(() => setDrafts([]))
      .finally(() => setLoadingDrafts(false));
  }, []);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const handlePublish = async (caseId) => {
    setPublishingId(caseId);
    try {
      await publishCase(caseId);
      setDrafts((prev) => prev.filter((d) => d.id !== caseId));
      fetchCases();
    } catch {
      alert('Could not publish that case. Please try again.');
    } finally {
      setPublishingId(null);
    }
  };

  const standardCases = useMemo(
    () => uploadedCases.filter((c) => (c.source || 'custom') === 'standard'),
    [uploadedCases]
  );
  const customCases = useMemo(
    () => uploadedCases.filter((c) => (c.source || 'custom') === 'custom'),
    [uploadedCases]
  );

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <div className="title-name-container">
            <header className="name-header">Simulation Cases</header>
          </div>
        </div>
        <div className="reload-button-container">
          <button className="reload-button" onClick={() => navigate('/admin/cases/chat')}>
            + New Case
          </button>
        </div>
      </div>

      <div className="component-container simulation-cases-component-container">
        <div className="cases-view-toggle-row">
          <div className="cases-view-toggle">
            <button
              className={`cases-view-toggle-btn ${activeTab === 'published' ? 'cases-view-toggle-btn-active' : ''}`}
              onClick={() => setActiveTab('published')}
            >
              Published
            </button>
            <button
              className={`cases-view-toggle-btn ${activeTab === 'draft' ? 'cases-view-toggle-btn-active' : ''}`}
              onClick={() => setActiveTab('draft')}
            >
              Draft
            </button>
          </div>
        </div>

        {activeTab === 'draft' ? (
          <div className="cases-section">
            <h2 className="cases-section-heading">Drafts <span className="cases-section-hint">private until published</span></h2>
            {loadingDrafts ? (
              <div className="cases-loading">Loading your drafts...</div>
            ) : drafts.length === 0 ? (
              <p className="chat-card-hint">No drafts yet.</p>
            ) : (
              <div className="cases-list">
                {drafts.map((draft) => (
                  <DraftCard
                    key={draft.id}
                    draft={draft}
                    onClick={() => goToCase(draft.id, 'draft')}
                    onPublish={handlePublish}
                    publishing={publishingId === draft.id}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {loadingUploaded && <div className="cases-loading cases-loading-padded">Loading uploaded cases...</div>}

            {customCases.length > 0 && (
              <div className="cases-section">
                <h2 className="cases-section-heading">UNC Cases</h2>
                <div className="cases-list">
                  {customCases.map((simCase) => (
                    <CaseCard key={simCase.id} simCase={simCase} onClick={() => goToCase(simCase.id, 'published')} />
                  ))}
                </div>
              </div>
            )}

            {standardCases.length > 0 && (
              <div className="cases-section">
                <h2 className="cases-section-heading">Kabilah Cases</h2>
                <div className="cases-list">
                  {standardCases.map((simCase) => (
                    <CaseCard key={simCase.id} simCase={simCase} onClick={() => goToCase(simCase.id, 'published')} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default SimulationCases;
